import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  Shield,
  Calendar,
  ArrowRight,
  Package,
  ExternalLink,
} from 'lucide-react';
import { api } from '../services/api';
import ActivityFeed from '../components/ActivityFeed';
import { formatDistanceToNow, format } from 'date-fns';
import { de } from 'date-fns/locale';
import clsx from 'clsx';

export default function Dashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: api.dashboard.getStats,
    refetchInterval: 30000,
  });

  const { data: activity, isLoading: activityLoading } = useQuery({
    queryKey: ['dashboard-activity'],
    queryFn: () => api.dashboard.getActivity(10),
    refetchInterval: 30000,
  });

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const totalProjects = stats?.totalProjects || 0;
  const okCount = stats?.ok || 0;
  const warningCount = stats?.warning || 0;
  const errorCount = stats?.error || 0;
  const unknownCount = stats?.unknown || 0;

  // Calculate percentages for status bar
  const getPercentage = (count: number) =>
    totalProjects > 0 ? (count / totalProjects) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header with Status Overview */}
      <div className="card p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          {/* Project Count */}
          <div className="flex items-center gap-4">
            <div className="text-5xl font-bold text-gray-900">{totalProjects}</div>
            <div className="text-gray-600">
              <div className="text-lg font-medium">Projekte</div>
              <div className="text-sm">werden verwaltet</div>
            </div>
          </div>

          {/* Status Bar */}
          <div className="flex-1 max-w-2xl">
            <div className="flex items-center gap-4 mb-2">
              <StatusPill color="green" count={okCount} label="OK" />
              <StatusPill color="yellow" count={warningCount} label="Warnung" />
              <StatusPill color="red" count={errorCount} label="Fehler" />
              {unknownCount > 0 && (
                <StatusPill color="gray" count={unknownCount} label="Neu" />
              )}
            </div>
            {/* Visual progress bar */}
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
              {okCount > 0 && (
                <div
                  className="bg-green-500 transition-all duration-500"
                  style={{ width: `${getPercentage(okCount)}%` }}
                />
              )}
              {warningCount > 0 && (
                <div
                  className="bg-yellow-500 transition-all duration-500"
                  style={{ width: `${getPercentage(warningCount)}%` }}
                />
              )}
              {errorCount > 0 && (
                <div
                  className="bg-red-500 transition-all duration-500"
                  style={{ width: `${getPercentage(errorCount)}%` }}
                />
              )}
              {unknownCount > 0 && (
                <div
                  className="bg-gray-300 transition-all duration-500"
                  style={{ width: `${getPercentage(unknownCount)}%` }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Backups Today */}
        <QuickStatCard
          icon={Clock}
          iconColor="text-blue-500"
          iconBg="bg-blue-50"
          title="Backups heute"
          value={stats?.backupsToday || 0}
          subtitle={
            stats?.backupSuccessRate != null
              ? `${stats?.backupSuccessRate}% Erfolgsrate (7 Tage)`
              : 'Keine Daten'
          }
          subtitleColor={
            stats?.backupSuccessRate != null && stats?.backupSuccessRate < 90
              ? 'text-yellow-600'
              : 'text-gray-500'
          }
        />

        {/* Last Validator Run */}
        <QuickStatCard
          icon={Shield}
          iconColor={
            stats?.lastValidatorRun?.status === 'PASSED'
              ? 'text-green-500'
              : stats?.lastValidatorRun?.status === 'FAILED'
              ? 'text-red-500'
              : 'text-gray-500'
          }
          iconBg={
            stats?.lastValidatorRun?.status === 'PASSED'
              ? 'bg-green-50'
              : stats?.lastValidatorRun?.status === 'FAILED'
              ? 'bg-red-50'
              : 'bg-gray-50'
          }
          title="Letzte Validierung"
          value={
            stats?.lastValidatorRun
              ? stats.lastValidatorRun.status === 'PASSED'
                ? 'Bestanden'
                : 'Fehlgeschlagen'
              : '-'
          }
          subtitle={
            stats?.lastValidatorRun
              ? `${stats.lastValidatorRun.projectName} - ${formatDistanceToNow(
                  new Date(stats.lastValidatorRun.createdAt),
                  { addSuffix: true, locale: de }
                )}`
              : 'Noch keine Validierung'
          }
        />

        {/* Next Scheduled Backup */}
        <QuickStatCard
          icon={Calendar}
          iconColor="text-purple-500"
          iconBg="bg-purple-50"
          title="Nächstes Backup"
          value={
            stats?.nextScheduledBackup
              ? format(new Date(stats.nextScheduledBackup.scheduledAt), 'HH:mm', {
                  locale: de,
                })
              : '-'
          }
          subtitle={
            stats?.nextScheduledBackup
              ? `${stats.nextScheduledBackup.projectName} - ${format(
                  new Date(stats.nextScheduledBackup.scheduledAt),
                  'dd.MM.',
                  { locale: de }
                )}`
              : 'Kein Backup geplant'
          }
        />

        {/* Pending Updates */}
        <QuickStatCard
          icon={Package}
          iconColor={
            (stats?.pendingUpdates?.plugins || 0) + (stats?.pendingUpdates?.themes || 0) > 0
              ? 'text-orange-500'
              : 'text-green-500'
          }
          iconBg={
            (stats?.pendingUpdates?.plugins || 0) + (stats?.pendingUpdates?.themes || 0) > 0
              ? 'bg-orange-50'
              : 'bg-green-50'
          }
          title="Ausstehende Updates"
          value={
            (stats?.pendingUpdates?.plugins || 0) + (stats?.pendingUpdates?.themes || 0)
          }
          subtitle={
            stats?.pendingUpdates
              ? `${stats.pendingUpdates.plugins} Plugins, ${stats.pendingUpdates.themes} Themes`
              : 'Keine Updates'
          }
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Attention Required - Takes 3 columns */}
        <div className="lg:col-span-3 card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Handlungsbedarf
            </h2>
            {(stats?.attentionProjects?.length || 0) > 0 && (
              <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                {stats?.attentionProjects?.length} Projekte
              </span>
            )}
          </div>

          {!stats?.attentionProjects || stats.attentionProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-gray-600 font-medium">Alles in Ordnung!</p>
              <p className="text-gray-500 text-sm mt-1">
                Keine Projekte erfordern aktuell Aufmerksamkeit
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {stats.attentionProjects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="block p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div
                        className={clsx(
                          'p-2 rounded-lg flex-shrink-0',
                          project.status === 'ERROR'
                            ? 'bg-red-100'
                            : 'bg-yellow-100'
                        )}
                      >
                        {project.status === 'ERROR' ? (
                          <XCircle className="w-5 h-5 text-red-500" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-yellow-500" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {project.name}
                        </div>
                        <div className="text-sm text-gray-500 truncate flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          {project.url.replace(/^https?:\/\//, '')}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {project.reasons.map((reason, idx) => (
                            <span
                              key={idx}
                              className={clsx(
                                'px-2 py-0.5 text-xs rounded-full',
                                project.status === 'ERROR'
                                  ? 'bg-red-50 text-red-700'
                                  : 'bg-yellow-50 text-yellow-700'
                              )}
                            >
                              {reason}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 flex-shrink-0 mt-1" />
                  </div>
                </Link>
              ))}

              {errorCount + warningCount > 5 && (
                <Link
                  to="/projects?status=ERROR"
                  className="block text-center py-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Alle {errorCount + warningCount} Projekte anzeigen
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Activity Feed - Takes 2 columns */}
        <div className="lg:col-span-2 card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Aktivität</h2>
            <Link
              to="/logs"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Alle Logs
            </Link>
          </div>
          {activityLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <ActivityFeed logs={activity || []} />
          )}
        </div>
      </div>

      {/* Last Backup Info */}
      {stats?.lastBackup && (
        <div className="card p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm text-gray-600">
                Letztes erfolgreiches Backup:{' '}
                <span className="font-medium text-gray-900">
                  {stats.lastBackup.projectName}
                </span>{' '}
                -{' '}
                {formatDistanceToNow(new Date(stats.lastBackup.createdAt), {
                  addSuffix: true,
                  locale: de,
                })}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Status Pill Component
function StatusPill({
  color,
  count,
  label,
}: {
  color: 'green' | 'yellow' | 'red' | 'gray';
  count: number;
  label: string;
}) {
  const colorClasses = {
    green: 'bg-green-100 text-green-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    red: 'bg-red-100 text-red-700',
    gray: 'bg-gray-100 text-gray-600',
  };

  return (
    <div
      className={clsx(
        'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium',
        colorClasses[color]
      )}
    >
      <span className="font-bold">{count}</span>
      <span className="hidden sm:inline">{label}</span>
    </div>
  );
}

// Quick Stat Card Component
function QuickStatCard({
  icon: Icon,
  iconColor,
  iconBg,
  title,
  value,
  subtitle,
  subtitleColor = 'text-gray-500',
}: {
  icon: typeof Clock;
  iconColor: string;
  iconBg: string;
  title: string;
  value: string | number;
  subtitle: string;
  subtitleColor?: string;
}) {
  return (
    <div className="card p-4">
      <div className="flex items-start gap-3">
        <div className={clsx('p-2 rounded-lg', iconBg)}>
          <Icon className={clsx('w-5 h-5', iconColor)} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-gray-600">{title}</div>
          <div className="text-2xl font-bold text-gray-900 mt-0.5">{value}</div>
          <div className={clsx('text-xs mt-1 truncate', subtitleColor)}>
            {subtitle}
          </div>
        </div>
      </div>
    </div>
  );
}
